import React from "react";
import { DeleteIcon } from "@orijinworks/frontend-commons";
import { useGlobal } from 'reactn';
import { useCallback, useMemo, useRef, useState } from "react";
import { deleteAnnouncementFeed, getAnnouncementById, getAnnouncementReadRecipients } from "../../../util/APIUtils";
import store, { changeToastType, showToast as showToastAction } from "../../core/store";
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';

/**
 * Custom hook for managing announcement menu functionality and deletion
 * Provides menu options for announcements and handles the delete confirmation modal workflow
 * 
 * @param {Function} onAnnouncementDeleteHandlerCallback - Callback function to execute after successful deletion.
 *   Receives the deleted announcement ID as a parameter. This is typically used to update the UI by removing
 *   the deleted announcement from the list or refreshing the announcements data.
 * @returns {Object} Hook return object containing:
 *   @returns {boolean} isLoading - Indicates whether a delete operation is currently in progress
 *   @returns {Function} getAnnouncementMenuOptions - Function that returns menu options for an announcement.
 *     Accepts an announcement object and returns an array of menu option objects with icon, text, and onClick handler
 *   @returns {React.RefObject} modalRef - Ref object to be attached to the DeleteModal component for controlling modal visibility
 *   @returns {Function} onAnnouncementDeleteHandler - Handler function to initiate the delete process for an announcement.
 *     Accepts an announcement ID, stores it in state, and opens the delete confirmation modal
 *   @returns {Function} onCloseDeleteModalHandler - Handler function to close the delete confirmation modal
 *   @returns {Function} onContinueDeleteModalHandler - Async handler function that executes the actual deletion.
 *     Calls the delete API, shows success/error toast messages, executes the callback, and closes the modal
 * 
 * @example
 * const {
 *   isLoading,
 *   getAnnouncementMenuOptions,
 *   modalRef,
 *   onCloseDeleteModalHandler,
 *   onContinueDeleteModalHandler
 * } = useAnnouncementMenuHook((announcementId) => {
 *   // Remove announcement from list
 *   setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
 * });
 */
export const useAnnouncementMenuHook = (onAnnouncementDeleteHandlerCallback) => {
    const [announcementId, setAnnouncementId] = useState(null);
    const modalRef = useRef(null);
    const infoModalRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInfoModalLoading, setIsInfoModalLoading] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [infoModalData, setInfoModalData] = useState({ 
        type: null, 
        members: [], 
        badgeLabel: 0 
    });

    /**
     * Toggles the visibility of the delete confirmation modal
     * Calls the toggleModal method on the modal reference and updates state
     */
    const toggleModal = useCallback(() => {
        setIsDeleteModalOpen(prev => !prev);
        if (modalRef.current) {
            modalRef.current.toggleModal();
        }
    }, []);

    /**
     * Displays a toast notification with the specified type and message
     * 
     * @param {string} type - The type of toast to display ('success', 'error', etc.)
     * @param {string} msg - The message to display in the toast
     */
    const showToast = useCallback((type, msg) => {
        store.dispatch(changeToastType(type));
        store.dispatch(showToastAction(msg));
    }, []);

    /**
     * Handles the initiation of announcement deletion
     * Stores the announcement ID and opens the delete confirmation modal
     * 
     * @param {string|number} id - The ID of the announcement to be deleted
     */
    const onAnnouncementDeleteHandler = useCallback((id) => {
        setAnnouncementId(id);
        toggleModal();
    }, [toggleModal]);

    /**
     * Handles closing the delete confirmation modal
     */
    const onCloseDeleteModalHandler = useCallback(() => {
        toggleModal();
    }, [toggleModal]);

    /**
     * Transforms membersInfo API response data into members array format
     * Sorts by type: locations first, then groups, then persons
     * 
     * @param {Array} membersInfo - Array of member objects from API response
     * @returns {Array<string>} Array of strings containing member names, sorted by type
     */
    const transformMembersInfoToMembers = useCallback((membersInfo) => {
        if (!membersInfo || !Array.isArray(membersInfo)) {
            return [];
        }
        
        // Sort by type: locations first, then groups, then persons
        const typeOrder = { location: 0, group: 1, person: 2 };
        const sorted = [...membersInfo].sort((a, b) => {
            const typeA = (a.type || '').toLowerCase();
            const typeB = (b.type || '').toLowerCase();
            const orderA = typeOrder[typeA] !== undefined ? typeOrder[typeA] : 99;
            const orderB = typeOrder[typeB] !== undefined ? typeOrder[typeB] : 99;
            return orderA - orderB;
        });
        
        return sorted.map(item => item.name || '').filter(name => name.length > 0);
    }, []);

    /**
     * Handles clicking the Members menu option
     * Uses provided membersInfo if available, otherwise calls the API to get announcement details by ID
     * Displays members in a modal
     * 
     * @param {string|number} id - The ID of the announcement
     * @param {Array|null} membersInfo - Optional membersInfo array. If provided, uses it directly without API call
     */
    const onMembersClickHandler = useCallback(async (id, membersInfo = null) => {
        setIsInfoModalLoading(true);
        try {
            let membersInfoToUse = membersInfo;
            
            // If membersInfo is not provided, fetch it from API (backward compatible)
            if (!membersInfoToUse) {
                const response = await getAnnouncementById(id);
                
                if (response && response.data && response.data.membersInfo && Array.isArray(response.data.membersInfo)) {
                    membersInfoToUse = response.data.membersInfo;
                }
            }
            
            // Process membersInfo if available
            if (membersInfoToUse && Array.isArray(membersInfoToUse)) {
                const members = transformMembersInfoToMembers(membersInfoToUse);
                
                // Store data for modal
                setInfoModalData({
                    type: 'members',
                    members: members.length > 0 ? members : ["No Members for this Announcement"],
                    badgeLabel: members.length > 0 ? membersInfoToUse.length : 0
                });
                
                // Open the modal
                if (infoModalRef.current) {
                    infoModalRef.current.toggleModal();
                }
            } else {
                // Handle case where membersInfo is not available
                setInfoModalData({
                    type: 'members',
                    members: ["No Members for this Announcement"],
                    badgeLabel: 0
                });
                if (infoModalRef.current) {
                    infoModalRef.current.toggleModal();
                }
            }
        } catch (error) {
            showToast("error", "Something went wrong while trying to fetch announcement details, please try again after few minutes or try reloading the application!");
        } finally {
            setIsInfoModalLoading(false);
        }
    }, [showToast, transformMembersInfoToMembers]);

    /**
     * Transforms read recipients API response data into members array format
     * Uses fullName from the API response
     * 
     * @param {Array} data - Array of read recipient objects from API response
     * @returns {Array<string>} Array of strings containing full names
     */
    const transformReadReceiptsDataToMembers = useCallback((data) => {
        if (!data || !Array.isArray(data)) {
            return [];
        }
        
        return data.map(item => item.fullName || '').filter(name => name.length > 0);
    }, []);

    /**
     * Handles clicking the Read Receipts menu option
     * Calls the API to get read recipients for the announcement and displays them in a modal
     * 
     * @param {string|number} id - The ID of the announcement
     */
    const onReadReceiptsClickHandler = useCallback(async (id) => {
        setIsInfoModalLoading(true);
        try {
            const response = await getAnnouncementReadRecipients(id);
            
            if (response && response.data && Array.isArray(response.data)) {
                const members = transformReadReceiptsDataToMembers(response.data);
                
                // Store data for modal
                setInfoModalData({
                    type: 'readReceipts',
                    members: members.length > 0 ? members : ["No Read Receipts for this Announcement"],
                    badgeLabel: members.length > 0 ? response.data.length : 0
                });
                
                // Open the modal
                if (infoModalRef.current) {
                    infoModalRef.current.toggleModal();
                }
            } else {
                // Handle case where response doesn't have data array
                setInfoModalData({
                    type: 'readReceipts',
                    members: ["No Read Receipts for this Announcement"],
                    badgeLabel: 0
                });
                if (infoModalRef.current) {
                    infoModalRef.current.toggleModal();
                }
            }
        } catch (error) {
            showToast("error", "Something went wrong while trying to fetch read recipients, please try again after few minutes or try reloading the application!");
        } finally {
            setIsInfoModalLoading(false);
        }
    }, [showToast, transformReadReceiptsDataToMembers]);

    /**
     * Handles the confirmation of announcement deletion
     * Performs the actual API call to delete the announcement, handles success/error states,
     * shows appropriate toast messages, and executes the callback function
     */
    const onContinueDeleteModalHandler = useCallback(async () => {
        setIsLoading(true);
        const response = await deleteAnnouncementFeed(announcementId);
        if (response) {
            onAnnouncementDeleteHandlerCallback(announcementId);
            showToast("success", "Announcement deleted successfully");
        } else {
            showToast("error", "Something went wrong while trying to delete announcement, please try again after few minutes or try reloading the application!");
        }
        setIsLoading(false);
        toggleModal();
    }, [announcementId, onAnnouncementDeleteHandlerCallback, showToast, toggleModal]);

    /**
     * Returns menu options configuration for an announcement
     * Provides "Members", "Read Receipts", and "Delete" options with appropriate styling and click handlers
     * 
     * @param {Object} announcement - The announcement object containing announcement details
     * @param {string|number} announcement.id - The unique identifier of the announcement
     * @param {Array} [announcement.membersInfo] - Optional membersInfo array. If provided, will be used directly without API call
     * @returns {Array<Object>} Array of menu option objects, each containing:
     *   - icon: React element to display as the menu option icon
     *   - text: Display text for the menu option
     *   - textSx: Style object for the text
     *   - onClick: Function to execute when the menu option is clicked
     */
    const getAnnouncementMenuOptions = useCallback((announcement) => {
        // Check if membersInfo is available in the announcement object
        const membersInfo = announcement.membersInfo;
        
        return [
            {
                icon: <PeopleAltOutlinedIcon sx={{ color: "#061E44" }} />,
                text: "Members",
                textSx: {
                    color: "#061E44"
                },
                onClick: () => onMembersClickHandler(announcement.id, membersInfo)
            },
            {
                icon: <DoneAllIcon sx={{ color: "#061E44" }} />,
                text: "Read Receipts",
                textSx: {
                    color: "#061E44"
                },
                onClick: () => onReadReceiptsClickHandler(announcement.id)
            },
            {
                icon: <DeleteIcon sx={{ color: "var(--red-dark)" }} />,
                text: "Remove from Feed",
                textSx: {
                    color: "var(--red-dark)"
                },
                onClick: () => onAnnouncementDeleteHandler(announcement.id)
            }
        ]
    }, [onAnnouncementDeleteHandler, onMembersClickHandler, onReadReceiptsClickHandler]);

    /**
     * Computed modal props based on the current infoModalData type
     * Returns the appropriate icon and heading for members or read receipts
     */
    const infoModalProps = useMemo(() => {
        if (infoModalData.type === 'members') {
            return {
                headingIcon: <PeopleAltOutlinedIcon sx={{ color: "#0F48B8" }} />,
                heading: "Members"
            };
        } else if (infoModalData.type === 'readReceipts') {
            return {
                headingIcon: <DoneAllIcon sx={{ color: "#0F48B8" }} />,
                heading: "Read Receipts"
            };
        }
        return {
            headingIcon: null,
            heading: ""
        };
    }, [infoModalData.type]);

    return {
        isLoading,
        getAnnouncementMenuOptions,
        modalRef,
        isDeleteModalOpen,
        onAnnouncementDeleteHandler,
        onCloseDeleteModalHandler,
        onContinueDeleteModalHandler,
        infoModalRef,
        isInfoModalLoading,
        infoModalData,
        infoModalProps
    }
}